'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useMutation } from '@apollo/client/react';

import { createResumableUpload, isValidInsightId } from '@/shared-utilities';
import {
  CREATE_SURVEY_SIGNED_UPLOAD_URL,
  SignedUrlAction,
  UPDATE_INSIGHT_RECORDING,
} from '@/survey-graphql';

import { stateManager } from './project-usability-recording-state-manager';
import { initResumableUploadPipelineOnce } from './project-usability-recording-upload.helpers';

interface UseUploadManagerOptions {
  insightId: string | null;
}

export function useUploadManager({ insightId }: UseUploadManagerOptions) {
  const initializedForInsightIdRef = useRef<string | null>(null);
  const [createSignedUrl] = useMutation(CREATE_SURVEY_SIGNED_UPLOAD_URL);
  const [updateInsightRecording] = useMutation(UPDATE_INSIGHT_RECORDING);
  const [error, setError] = useState<string | null>(null);
  // One-shot guard against React strict-mode double-invoke and rapid
  // insightId stability flips producing two parallel init attempts.
  const initStartedRef = useRef<string | null>(null);

  const initializeUpload = useCallback(async () => {
    if (!insightId) return;
    if (!isValidInsightId(insightId)) return;
    if (initStartedRef.current === insightId) return;
    initStartedRef.current = insightId;

    try {
      const fileName = `usability-recording-${insightId}.webm`;
      const { data } = await createSignedUrl({
        variables: {
          input: {
            fileName,
            mimeType: 'video/webm',
            isPublic: false,
            action: SignedUrlAction.Resumable,
          },
        },
        onCompleted: (data) => {
          const filePath = data?.createSurveySignedUploadUrl?.path;
          if (!filePath) return;
          void updateInsightRecording({
            variables: {
              input: { insightId, gcsKey: filePath },
            },
          });
        },
      });

      const signedUrl = data?.createSurveySignedUploadUrl?.url;
      if (!signedUrl || typeof signedUrl !== 'string') {
        throw new Error('Failed to obtain signed URL');
      }

      const upload = createResumableUpload(signedUrl);
      stateManager.setUpload(upload);

      upload
        .getSessionUriPromise()
        .then((sessionUri) => {
          updateInsightRecording({
            variables: {
              input: { insightId, sessionUri },
            },
          }).catch(() => {
            // Silently fail - session URI update is best effort
          });
        })
        .catch(() => {
          // Silently fail if session URI never becomes available
        });
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : 'Upload initialization failed';
      setError(errorMessage);
      initStartedRef.current = null;
    }
  }, [insightId, createSignedUrl, updateInsightRecording]);

  useEffect(
    () =>
      initResumableUploadPipelineOnce({
        insightId,
        initializedForInsightIdRef,
        initializeUpload,
      }),
    [insightId, initializeUpload]
  );

  return { error };
}
